<template>
  <div class="edit-container-goods" v-loading="loading">
    <div class="edit-container-goods__toolbar">
      <div class="edit-container-goods__title">
        {{ $l('section_assignment_detail') || '分配明细' }}
        <span class="edit-container-goods__count">
          {{ tableData.length }} {{ $l('INDEX.label_item') || '项' }}
        </span>
      </div>

      <div class="edit-container-goods__actions">
        <group-toggle-button
          v-if="hasGroups"
          :expanded="allGroupsExpanded"
          @toggle="toggleAllGroups"
        />
        <BatchRemoveButton
          v-if="canCheckGoods"
          :checkedCount="checkedSummary.skuCount"
          :disabled="!canCheckGoods"
          @batch-remove="removeCheckedGoods"
        />
        <group-popover
          :groupFields="groupFields"
          :groupableFields="groupableFields"
          @change-group-fields="$emit('change-group-fields', $event)"
        />
      </div>
    </div>

    <PageMain
      ref="main"
      :tableData="tableData"
      :groupFields="groupFields"
      :isBooked="currentIsBooked"
      @update-goods="updateGoods"
      @remove-goods="removeGoods"
      @checkbox-change="handleCheckboxChange"
      @expand-state-change="allGroupsExpanded = $event"
      @group-state-change="hasGroups = $event"
    />
    <batch-summary-footer
      v-if="canCheckGoods"
      :checkedSummary="checkedSummary"
      :showWeight="true"
    />
  </div>
</template>

<script>
import EditLoading from '@/mixins/edit-loading'
import { add, round } from '@/libs/util.number'
import BatchRemoveButton from './batch-remove-button.vue'
import PageMain from './main.vue'
import GroupPopover from '../pending-goods-pool/group-popover.vue'
import GroupToggleButton from '../pending-goods-pool/group-toggle-button.vue'
import BatchSummaryFooter from '../pending-goods-pool/batch-summary-footer.vue'

export default {
  components: {
    BatchRemoveButton,
    PageMain,
    GroupPopover,
    GroupToggleButton,
    BatchSummaryFooter,
  },
  mixins: [EditLoading],
  props: {
    detail: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
    groupFields: { type: Array, default: () => [] },
    groupableFields: { type: Array, default: () => [] },
    isBooked: { type: Boolean, default: false },
  },
  data() {
    return {
      checkedRows: [],
      allGroupsExpanded: false,
      hasGroups: false,
    }
  },
  watch: {
    groupFields() {
      this.allGroupsExpanded = false
      this.hasGroups = false
    },
  },
  computed: {
    tableData() {
      return this.detail.listGoods || []
    },
    currentIsBooked() {
      return this.isBooked || !!this.detail.shipmentBookingCode
    },
    canCheckGoods() {
      return !this.currentIsBooked
    },
    checkedSummary() {
      return this.getRowsSummary(this.checkedRows)
    },
  },
  methods: {
    reload() {
      this.$emit('reload')
    },
    handleCheckboxChange(rows = []) {
      this.checkedRows = rows
    },
    toggleAllGroups() {
      if (!this.$refs.main) return
      this.$refs.main.toggleAllGroups()
    },
    removeCheckedGoods() {
      if (!this.checkedRows.length) return
      this.removeGoods({ goodsRows: this.checkedRows })
    },
    async updateGoods({ goodsRow, value }) {
      if (this.loadingOpen()) return
      const res = await this.$api
        .ecomShipmentContainerChangeGoodsApi({
          shipmentContainerGoodsCode: goodsRow.shipmentContainerGoodsCode,
          containerBoxQty: value,
        })
        .catch(() => null)
      this.loadingClose()
      if (!res) return this.reload()

      this.$message({
        type: 'success',
        message: res.msg || this.$l('message_save_ok_simple') || '保存成功',
      })
      this.reload()
    },
    async removeGoods({ goodsRow, goodsRows = [], tip }) {
      if (this.currentIsBooked) return

      const rows = goodsRows.length ? goodsRows : [goodsRow]
      const listShipmentContainerGoodsCode = [
        ...new Set(
          rows.map((row) => row?.shipmentContainerGoodsCode).filter(Boolean)
        ),
      ]

      if (!listShipmentContainerGoodsCode.length) return

      const confirmMessage = goodsRows.length
        ? this.renderBatchConfirmMessage(
            tip ||
              this.$l('tips_confirm_batch_remove_assignment') ||
              '确认批量移除选中的分配记录吗？',
            this.getRowsSummary(rows)
          )
        : this.$l('tips_confirm_remove_assignment') || '确认移除该分配记录吗？'
      const confirm = await this.$confirm(
        confirmMessage,
        this.$l('tips_confirm_title') || '提示',
        {
          type: 'warning',
        }
      )
        .then(() => true)
        .catch(() => false)
      if (!confirm) return

      if (this.loadingOpen()) return
      const res = await this.$api
        .ecomShipmentContainerRemoveGoodsApi({
          listShipmentContainerGoodsCode,
        })
        .catch(() => null)
      this.loadingClose()
      if (!res) return

      this.$message({
        type: 'success',
        message: res.msg || this.$l('message_remove_ok') || '移除成功',
      })
      this.checkedRows = []
      this.reload()
    },
    getRowsSummary(rows = []) {
      return {
        skuCount: rows.length,
        totalCtnQty: round(
          rows.reduce(
            (sum, row) => add(sum, Number(row.containerBoxQty || 0)),
            0
          ),
          2
        ),
        totalVolume: round(
          rows.reduce((sum, row) => add(sum, Number(row.totalVolume || 0)), 0),
          2
        ),
        totalWeight: round(
          rows.reduce(
            (sum, row) => add(sum, Number(row.totalGrossWeight || 0)),
            0
          ),
          2
        ),
      }
    },
    renderBatchConfirmMessage(tip, summary) {
      const h = this.$createElement
      const items = [
        {
          label: this.$l('label_sku_count') || 'SKU数量',
          value: summary.skuCount,
        },
        {
          label: this.$l('form_label_totalCtnQty') || '总箱数',
          value: summary.totalCtnQty,
        },
        {
          label: this.$l('label_total_volume') || '总体积',
          value: `${summary.totalVolume} m³`,
        },
        {
          label: this.$l('label_total_gross_weight') || '总毛重',
          value: `${summary.totalWeight} kg`,
        },
      ]

      return h('div', [
        h(
          'div',
          {
            style: {
              marginBottom: '8px',
              color: '#303133',
            },
          },
          tip
        ),
        h(
          'div',
          {
            style: {
              padding: '8px 10px',
              background: '#f8fafc',
              border: '1px solid #e5e7eb',
              borderRadius: '4px',
            },
          },
          items.map((item) =>
            h(
              'div',
              {
                style: {
                  display: 'flex',
                  justifyContent: 'space-between',
                  lineHeight: '22px',
                  fontSize: '12px',
                },
              },
              [
                h('span', { style: { color: '#606266' } }, item.label),
                h(
                  'strong',
                  { style: { color: '#303133', marginLeft: '12px' } },
                  String(item.value)
                ),
              ]
            )
          )
        ),
      ])
    },
  },
}
</script>

<style lang="scss" scoped>
.edit-container-goods {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  background: #fff;
}

.edit-container-goods__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
  padding: 8px 10px 8px;
  border-top: 1px solid #eef2f6;
  flex-shrink: 0;
  box-sizing: border-box;
}

.edit-container-goods__title {
  display: flex;
  align-items: center;
  color: #344054;
  font-size: 12px;
  font-weight: 600;
}

.edit-container-goods__count {
  margin-left: 6px;
  color: #98a2b3;
  font-size: 12px;
  font-weight: 400;
}

.edit-container-goods__actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

::v-deep .edit-container-goods__actions .pool-action-btn {
  height: 24px;
  padding: 0 9px;
  font-size: 11px;
}

::v-deep .edit-container-goods__actions .pool-action-btn i {
  font-size: 12px;
}

::v-deep .edit-container-goods__actions .group-toggle-button {
  height: 24px;
  padding: 0 9px;
  font-size: 11px;
}

::v-deep .edit-container-goods__actions .group-toggle-button i {
  font-size: 12px;
}
</style>
